/**
 * Zentrale Spiellogik-Definitionen (Single Source of Truth)
 *
 * Diese Datei ist die EINZIGE Quelle für Karten- und Spiellogik.
 * Beide Client und Server importieren von hier.
 *
 * Verwendung:
 * - Server: const { createDeck, beatsLast } = require('../../src/shared/core/gameLogic');
 * - Client: import { createDeck, beatsLast } from './shared/core/gameLogic';
 *
 * MIGRATION: Diese Datei ersetzt die duplizierten Dateien:
 * - src/gameLogic.js (deprecated, re-exports von hier)
 * - server/gameLogic.js (deprecated, re-exports von hier)
 */

// =============================================================================
// IMPORTS
// =============================================================================

const { generateSpecialCards, isSpecialCard } = require('../entities/specialCards');

// =============================================================================
// KONSTANTEN
// =============================================================================

// Ränge und Werte: 3 niedrigst (0), 2 höchst (12)
const ranks = ['3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A', '2'];
const rankValues = {};
ranks.forEach((rank, i) => rankValues[rank] = i);

const suits = ['♦', '♥', '♠', '♣']; // ♦ niedrigst (0), ♣ höchst (3)
const suitValues = {};
suits.forEach((suit, i) => suitValues[suit] = i);

// Play-Type Ranking für Vergleiche
const PLAY_RANKS = {
  'single': 0,
  'pair': 1,
  'triple': 2,
  'twoPairs': 3,
  'straight': 4,
  'flush': 5,
  'fullHouse': 6,
  'pokerWithKicker': 7,
  'straightFlush': 8,
  'royalFlush': 9
};

// =============================================================================
// DECK-FUNKTIONEN
// =============================================================================

/**
 * Erstellt ein neues 52-Karten-Deck
 * @returns {Array<{rank: string, suit: string, value: number}>}
 */
function createDeck() {
  const deck = [];
  ranks.forEach(rank => {
    suits.forEach(suit => {
      deck.push({ rank, suit, value: rankValues[rank] });
    });
  });
  return deck;
}

/**
 * Mischt ein Array (Fisher-Yates shuffle)
 * @param {Array} array - Das zu mischende Array
 * @returns {Array} Das gemischte Array (in-place)
 */
function shuffle(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

/**
 * Teilt Karten an Spieler aus
 * @param {number} numPlayers - Anzahl der Spieler
 * @param {number} cardsPerPlayer - Karten pro Spieler (min 6)
 * @param {Object} [specialCardConfig] - Optionale Spezialkarten-Konfiguration
 * @param {boolean} [specialCardConfig.enabled] - Spezialkarten aktivieren
 * @param {number} [specialCardConfig.probability] - Wahrscheinlichkeit (0-1)
 * @param {number} [specialCardConfig.maxPerDeck] - Max. Spezialkarten pro Deck
 * @returns {{hands: Array[], drawPile: Array, specialCardsAdded: number}}
 */
function dealCards(numPlayers, cardsPerPlayer, specialCardConfig = null) {
  if (cardsPerPlayer < 6) cardsPerPlayer = 6;
  let deck = shuffle(createDeck());

  // Spezialkarten hinzufügen wenn aktiviert
  let specialCardsAdded = 0;
  if (specialCardConfig?.enabled) {
    const specialCards = generateSpecialCards(deck.length, specialCardConfig);
    specialCardsAdded = specialCards.length;
    if (specialCards.length > 0) {
      deck = shuffle([...deck, ...specialCards]);
      console.log(`[dealCards] Added ${specialCards.length} special cards to deck`);
    }
  }

  const totalToDeal = Math.min(cardsPerPlayer * numPlayers, deck.length);
  const hands = Array.from({ length: numPlayers }, () => []);
  for (let i = 0; i < totalToDeal; i++) {
    hands[i % numPlayers].push(deck[i]);
  }
  const drawPile = deck.slice(totalToDeal);

  // Sortiere Hände (Wert, dann Suit)
  // Spezialkarten werden ans Ende sortiert (value = undefined → Infinity)
  hands.forEach(hand => {
    hand.sort((a, b) => {
      // Spezialkarten ans Ende
      if (isSpecialCard(a) && !isSpecialCard(b)) return 1;
      if (!isSpecialCard(a) && isSpecialCard(b)) return -1;
      if (isSpecialCard(a) && isSpecialCard(b)) return 0;
      // Normale Karten: Wert, dann Suit
      return a.value - b.value || suitValues[a.suit] - suitValues[b.suit];
    });
  });

  return { hands, drawPile, specialCardsAdded };
}

// =============================================================================
// SPIELER-FUNKTIONEN
// =============================================================================

/**
 * Findet den Startspieler (niedrigste Karte)
 * @param {Array[]} hands - Array von Spieler-Händen
 * @returns {number} Index des Startspielers
 */
function findStartPlayer(hands) {
  const minCards = hands.map(hand => hand.length ? hand.reduce((min, c) => {
    const minScore = min.value + suitValues[min.suit] / 10;
    const cScore = c.value + suitValues[c.suit] / 10;
    return cScore < minScore ? c : min;
  }) : null).filter(Boolean);
  if (!minCards.length) return 0;
  let startIdx = 0;
  let minScore = Infinity;
  minCards.forEach((card, i) => {
    const score = card.value + suitValues[card.suit] / 10;
    if (score < minScore) {
      minScore = score;
      startIdx = i;
    }
  });
  return startIdx;
}

// =============================================================================
// SPIELZUG-VALIDIERUNG
// =============================================================================

/**
 * Ermittelt den Typ eines Spielzugs
 * @param {Array} play - Die gespielten Karten
 * @returns {string|null} Der Spielzug-Typ oder null wenn ungültig
 */
function getPlayType(play) {
  if (!play || !play.length) return null;

  const values = play.map(c => c.value).sort((a, b) => a - b);
  const uniqueValues = new Set(values);
  const suitsSet = new Set(play.map(c => c.suit));

  if (play.length === 1) return 'single';
  if (play.length === 2 && uniqueValues.size === 1) return 'pair';
  if (play.length === 3 && uniqueValues.size === 1) return 'triple';

  // 4 cards: Two pairs (2+2 different values OR 4 of same value played as two pairs)
  if (play.length === 4) {
    const counts = values.reduce((acc, v) => ({ ...acc, [v]: (acc[v] || 0) + 1 }), {});
    const countValues = Object.values(counts).sort((a, b) => a - b);
    // Two different pairs (2+2) or four of a kind (4) both valid as twoPairs
    if (countValues.join(',') === '2,2' || countValues.join(',') === '4') {
      return 'twoPairs';
    }
  }

  if (play.length === 5) {
    const counts = values.reduce((acc, v) => ({ ...acc, [v]: (acc[v] || 0) + 1 }), {});
    if (Object.values(counts).includes(4) && Object.keys(counts).length === 2) return 'pokerWithKicker';

    let isStraight = false;
    if (uniqueValues.size === 5) {
      if (Math.max(...values) - Math.min(...values) === 4) isStraight = true;
      // Wrap-around: A-2-3-4-5
      else if (new Set(values).size === 5 && values.includes(12) && values.includes(0) && values.includes(1) && values.includes(2) && values.includes(3)) {
        isStraight = true;
      }
    }

    if (isStraight) {
      if (suitsSet.size === 1) {
        if (values.join(',') === '8,9,10,11,12') return 'royalFlush';
        return 'straightFlush';
      }
      return 'straight';
    }

    if (suitsSet.size === 1) return 'flush';
    if (Object.values(counts).sort().join(',') === '2,3') return 'fullHouse';
  }

  return null;
}

/**
 * Prüft ob ein Spielzug den letzten schlägt
 * @param {Array} lastPlay - Der letzte Spielzug
 * @param {Array} currentPlay - Der aktuelle Spielzug
 * @returns {boolean} True wenn currentPlay lastPlay schlägt
 */
function beatsLast(lastPlay, currentPlay) {
  if (!lastPlay.length) return true;
  if (currentPlay.length !== lastPlay.length) return false;

  const lastType = getPlayType(lastPlay);
  const currType = getPlayType(currentPlay);

  // Two Pairs comparison (4 cards)
  if (currType === 'twoPairs' && lastType === 'twoPairs') {
    const getHighPairValue = (play) => {
      const counts = play.reduce((acc, c) => ({ ...acc, [c.value]: (acc[c.value] || 0) + 1 }), {});
      // For 4-of-a-kind played as two pairs, just use that value
      if (Object.keys(counts).length === 1) return Number(Object.keys(counts)[0]);
      // For two different pairs, get the higher pair value
      return Math.max(...Object.keys(counts).map(Number));
    };
    const getHighPairSuit = (play, pairValue) => {
      const pairCards = play.filter(c => c.value === pairValue);
      return Math.max(...pairCards.map(c => suitValues[c.suit]));
    };

    const lastHighPair = getHighPairValue(lastPlay);
    const currHighPair = getHighPairValue(currentPlay);

    if (currHighPair > lastHighPair) return true;
    if (currHighPair < lastHighPair) return false;

    // Same high pair value - compare suit of highest card in that pair
    const lastHighSuit = getHighPairSuit(lastPlay, lastHighPair);
    const currHighSuit = getHighPairSuit(currentPlay, currHighPair);
    return currHighSuit > lastHighSuit;
  }

  if (currentPlay.length === 5) {
    if (PLAY_RANKS[currType] > PLAY_RANKS[lastType]) return true;
    if (PLAY_RANKS[currType] < PLAY_RANKS[lastType]) return false;

    // Gleiches Ranking: Spezielle Behandlung für verschiedene Typen
    if (PLAY_RANKS[currType] === PLAY_RANKS[lastType]) {
      const lastValues = lastPlay.map(c => c.value).sort((a, b) => a - b);
      const currValues = currentPlay.map(c => c.value).sort((a, b) => a - b);

      // Spezielle Behandlung für Flushes - FARBE vor WERT
      if (currType === 'flush' && lastType === 'flush') {
        const lastSuit = suitValues[lastPlay[0].suit];
        const currSuit = suitValues[currentPlay[0].suit];

        if (currSuit > lastSuit) return true;
        if (currSuit < lastSuit) return false;

        // Gleiche Farbe: Höchste Karte vergleichen
        let lastHigh = Math.max(...lastValues);
        let currHigh = Math.max(...currValues);
        return currHigh > lastHigh;
      }

      // Spezielle Behandlung für Straights (ohne Flush)
      if (currType === 'straight' && lastType === 'straight') {
        const isWrapAroundLast = lastValues.includes(0) && lastValues.includes(1) && lastValues.includes(2) && lastValues.includes(3) && lastValues.includes(12);
        const isWrapAroundCurr = currValues.includes(0) && currValues.includes(1) && currValues.includes(2) && currValues.includes(3) && currValues.includes(12);
        let lastHigh = isWrapAroundLast ? 3 : Math.max(...lastValues);
        let currHigh = isWrapAroundCurr ? 3 : Math.max(...currValues);

        if (currHigh > lastHigh) return true;
        if (currHigh < lastHigh) return false;

        const lastHighCard = lastPlay.find(c => c.value === lastHigh);
        const currHighCard = currentPlay.find(c => c.value === currHigh);
        return suitValues[currHighCard.suit] > suitValues[lastHighCard.suit];
      }

      // Spezielle Behandlung für Straight Flushes
      if (currType === 'straightFlush' && lastType === 'straightFlush') {
        const isWrapAroundLast = lastValues.includes(0) && lastValues.includes(1) && lastValues.includes(2) && lastValues.includes(3) && lastValues.includes(12);
        const isWrapAroundCurr = currValues.includes(0) && currValues.includes(1) && currValues.includes(2) && currValues.includes(3) && currValues.includes(12);
        let lastHigh = isWrapAroundLast ? 3 : Math.max(...lastValues);
        let currHigh = isWrapAroundCurr ? 3 : Math.max(...currValues);

        if (currHigh > lastHigh) return true;
        if (currHigh < lastHigh) return false;

        return suitValues[currentPlay[0].suit] > suitValues[lastPlay[0].suit];
      }

      // Spezielle Behandlung für Full House - DRILLING vor PAAR
      if (currType === 'fullHouse' && lastType === 'fullHouse') {
        const lastCounts = lastPlay.reduce((acc, c) => ({ ...acc, [c.value]: (acc[c.value] || 0) + 1 }), {});
        const currCounts = currentPlay.reduce((acc, c) => ({ ...acc, [c.value]: (acc[c.value] || 0) + 1 }), {});
        const lastTriple = Number(Object.keys(lastCounts).find(k => lastCounts[k] === 3));
        const currTriple = Number(Object.keys(currCounts).find(k => currCounts[k] === 3));
        const lastPair = Number(Object.keys(lastCounts).find(k => lastCounts[k] === 2));
        const currPair = Number(Object.keys(currCounts).find(k => currCounts[k] === 2));

        if (currTriple > lastTriple) return true;
        if (currTriple < lastTriple) return false;
        return currPair > lastPair;
      }

      // Spezielle Behandlung für Poker mit Kicker - VIERLING-WERT entscheidet
      if (currType === 'pokerWithKicker' && lastType === 'pokerWithKicker') {
        const lastCounts = lastPlay.reduce((acc, c) => ({ ...acc, [c.value]: (acc[c.value] || 0) + 1 }), {});
        const currCounts = currentPlay.reduce((acc, c) => ({ ...acc, [c.value]: (acc[c.value] || 0) + 1 }), {});
        const lastQuad = Number(Object.keys(lastCounts).find(k => lastCounts[k] === 4));
        const currQuad = Number(Object.keys(currCounts).find(k => currCounts[k] === 4));

        if (currQuad > lastQuad) return true;
        if (currQuad < lastQuad) return false;
        // Bei gleichem Vierling: Kicker vergleichen
        const lastKicker = Number(Object.keys(lastCounts).find(k => lastCounts[k] === 1));
        const currKicker = Number(Object.keys(currCounts).find(k => currCounts[k] === 1));
        return currKicker > lastKicker;
      }

      // Für alle anderen 5-Karten-Kombinationen (Poker)
      let lastHigh = new Set(lastValues).size === 5 && lastValues.includes(0) && lastValues.includes(1) && lastValues.includes(2) && lastValues.includes(3) && lastValues.includes(12) ? 3 : Math.max(...lastValues);
      let currHigh = new Set(currValues).size === 5 && currValues.includes(0) && currValues.includes(1) && currValues.includes(2) && currValues.includes(3) && currValues.includes(12) ? 3 : Math.max(...currValues);

      if (currHigh > lastHigh) return true;
      if (currHigh < lastHigh) return false;

      // Tie: Suit der High Card
      const lastHighValue = lastHigh === 3 ? 3 : Math.max(...lastValues);
      const currHighValue = currHigh === 3 ? 3 : Math.max(...currValues);
      const lastHighCards = lastPlay.filter(c => c.value === lastHighValue);
      const currHighCards = currentPlay.filter(c => c.value === currHighValue);
      const lastMaxSuit = lastHighCards.reduce((max, c) => suitValues[c.suit] > suitValues[max.suit] ? c : max);
      const currMaxSuit = currHighCards.reduce((max, c) => suitValues[c.suit] > suitValues[max.suit] ? c : max);
      return suitValues[currMaxSuit.suit] > suitValues[lastMaxSuit.suit];
    }
    return false;
  }

  // Rest der Funktion für 1-4 Karten
  if (lastType !== currType) return false;
  if (PLAY_RANKS[currType] > PLAY_RANKS[lastType]) return true;

  if (['single', 'pair', 'triple'].includes(currType)) {
    const lastMax = lastPlay.reduce((max, c) => (c.value > max.value || (c.value === max.value && suitValues[c.suit] > suitValues[max.suit])) ? c : max);
    const currMax = currentPlay.reduce((max, c) => (c.value > max.value || (c.value === max.value && suitValues[c.suit] > suitValues[max.suit])) ? c : max);
    return currMax.value > lastMax.value || (currMax.value === lastMax.value && suitValues[currMax.suit] > suitValues[lastMax.suit]);
  }

  return Math.max(...currentPlay.map(c => c.value)) > Math.max(...lastPlay.map(c => c.value));
}

// =============================================================================
// KARTEN-AKTIONEN
// =============================================================================

/**
 * Zieht eine Karte vom Stapel
 * @param {Array} playerHand - Die Hand des Spielers
 * @param {Array} drawPile - Der Nachziehstapel
 * @returns {boolean} True wenn Karte gezogen wurde
 */
function drawCard(playerHand, drawPile) {
  if (!drawPile.length) return false;
  const card = drawPile.shift();
  playerHand.push(card);
  playerHand.sort((a, b) => a.value - b.value || suitValues[a.suit] - suitValues[b.suit]);
  return true;
}

// =============================================================================
// HILFSFUNKTIONEN
// =============================================================================

/**
 * Generiert alle k-Kombinationen eines Arrays
 * @param {Array} arr - Das Array
 * @param {number} k - Anzahl der Elemente pro Kombination
 * @returns {Array[]} Array von Kombinationen
 */
function combinations(arr, k) {
  if (k === 1) return arr.map(e => [e]);
  const comb = [];
  arr.forEach((e, i) => {
    combinations(arr.slice(i + 1), k - 1).forEach(c => comb.push([e, ...c]));
  });
  return comb;
}

/**
 * Generiert alle Paare aus Karten gleichen Werts
 */
function getAllPairs(cards) {
  const pairs = [];
  for (let i = 0; i < cards.length; i++) {
    for (let j = i + 1; j < cards.length; j++) {
      pairs.push([cards[i], cards[j]]);
    }
  }
  return pairs;
}

/**
 * Generiert alle Drillinge aus Karten gleichen Werts
 */
function getAllTriples(cards) {
  const triples = [];
  for (let i = 0; i < cards.length; i++) {
    for (let j = i + 1; j < cards.length; j++) {
      for (let k = j + 1; k < cards.length; k++) {
        triples.push([cards[i], cards[j], cards[k]]);
      }
    }
  }
  return triples;
}

/**
 * Findet alle gültigen Spielzüge
 * @param {Array} hand - Die Hand des Spielers
 * @param {Array} lastPlay - Der letzte Spielzug
 * @param {number[]} lengths - Erlaubte Spielzug-Längen
 * @param {boolean} isFirstTurn - Ob es der erste Zug ist
 * @param {Object|null} minCard - Die niedrigste Karte (muss beim ersten Zug enthalten sein)
 * @returns {Array[]} Array von gültigen Spielzügen
 */
function findValidPlays(hand, lastPlay, lengths = [1, 2, 3, 4, 5], isFirstTurn = false, minCard = null) {
  if (!hand || !Array.isArray(hand)) return [];

  const validPlays = [];
  const lastLen = lastPlay.length;
  const lens = lastPlay.length ? [lastLen] : lengths;

  lens.forEach(length => {
    if (length === 1) {
      hand.forEach(card => {
        const play = [card];
        if (beatsLast(lastPlay, play) && (!isFirstTurn || play.some(c => c.rank === minCard.rank && c.suit === minCard.suit))) {
          validPlays.push(play);
        }
      });
    } else if (length === 2) {
      const cardsByValue = hand.reduce((acc, c) => {
        acc[c.value] = acc[c.value] || [];
        acc[c.value].push(c);
        return acc;
      }, {});
      Object.values(cardsByValue).forEach(cards => {
        if (cards.length >= 2) {
          getAllPairs(cards).forEach(pair => {
            if (beatsLast(lastPlay, pair) && (!isFirstTurn || pair.some(c => c.rank === minCard.rank && c.suit === minCard.suit))) {
              validPlays.push(pair);
            }
          });
        }
      });
    } else if (length === 3) {
      const cardsByValue = hand.reduce((acc, c) => {
        acc[c.value] = acc[c.value] || [];
        acc[c.value].push(c);
        return acc;
      }, {});
      Object.values(cardsByValue).forEach(cards => {
        if (cards.length >= 3) {
          getAllTriples(cards).forEach(triple => {
            if (beatsLast(lastPlay, triple) && (!isFirstTurn || triple.some(c => c.rank === minCard.rank && c.suit === minCard.suit))) {
              validPlays.push(triple);
            }
          });
        }
      });
    } else if (length === 4) {
      const cardsByValue = hand.reduce((acc, c) => {
        acc[c.value] = acc[c.value] || [];
        acc[c.value].push(c);
        return acc;
      }, {});
      const pairValues = Object.keys(cardsByValue).filter(v => cardsByValue[v].length >= 2).map(Number);

      // Option 1: Four of a kind (played as two pairs)
      pairValues.forEach(v => {
        if (cardsByValue[v].length >= 4) {
          const possible = cardsByValue[v].slice(0, 4);
          if (beatsLast(lastPlay, possible) && (!isFirstTurn || possible.some(c => c.rank === minCard.rank && c.suit === minCard.suit))) {
            validPlays.push(possible);
          }
        }
      });

      // Option 2: Two different pairs
      for (let i = 0; i < pairValues.length; i++) {
        for (let j = i + 1; j < pairValues.length; j++) {
          const pairs1 = getAllPairs(cardsByValue[pairValues[i]]);
          const pairs2 = getAllPairs(cardsByValue[pairValues[j]]);
          pairs1.forEach(pair1 => {
            pairs2.forEach(pair2 => {
              const possible = [...pair1, ...pair2];
              if (beatsLast(lastPlay, possible) && (!isFirstTurn || possible.some(c => c.rank === minCard.rank && c.suit === minCard.suit))) {
                validPlays.push(possible);
              }
            });
          });
        }
      }
    } else if (length === 5) {
      const combs = combinations(hand, 5);
      combs.forEach(comb => {
        if (getPlayType(comb) && beatsLast(lastPlay, comb) && (!isFirstTurn || comb.some(c => c.rank === minCard.rank && c.suit === minCard.suit))) {
          validPlays.push(comb);
        }
      });
    }
  });

  return validPlays;
}

/**
 * Prüft ob Karten in der Hand sind
 * @param {Array} hand - Die Hand
 * @param {Array} cards - Die zu prüfenden Karten
 * @returns {boolean}
 */
function hasCards(hand, cards) {
  const handCopy = [...hand];
  for (const card of cards) {
    const index = handCopy.findIndex(c => c.rank === card.rank && c.suit === card.suit);
    if (index === -1) return false;
    handCopy.splice(index, 1);
  }
  return true;
}

/**
 * Entfernt Karten aus der Hand
 * @param {Array} hand - Die Hand
 * @param {Array} cards - Die zu entfernenden Karten
 * @returns {Array} Neue Hand ohne die Karten
 */
function removeCards(hand, cards) {
  const newHand = [...hand];
  for (const card of cards) {
    const index = newHand.findIndex(c => c.rank === card.rank && c.suit === card.suit);
    if (index !== -1) {
      newHand.splice(index, 1);
    }
  }
  return newHand;
}

// =============================================================================
// COMBO-DETECTION HELPER (für Siegel-Effekte)
// =============================================================================

/**
 * Prüft ob alle Karten zur gleichen Farbgruppe gehören (rot oder schwarz)
 * Rot: Herz (♥), Karo (♦)
 * Schwarz: Pik (♠), Kreuz (♣)
 * @param {Array} cards - Die zu prüfenden Karten
 * @returns {boolean} True wenn alle rot oder alle schwarz
 */
function isAllSameColorGroup(cards) {
  if (!cards || cards.length === 0) return false;

  const isRed = (suit) => suit === '♦' || suit === '♥';   // Karo, Herz
  const isBlack = (suit) => suit === '♠' || suit === '♣'; // Pik, Kreuz

  const firstIsRed = isRed(cards[0].suit);
  return cards.every(c => firstIsRed ? isRed(c.suit) : isBlack(c.suit));
}

/**
 * Prüft ob es ein 5-Card-Play ist
 * @param {Array} cards - Die gespielten Karten
 * @returns {boolean} True wenn genau 5 Karten
 */
function isFiveCardPlay(cards) {
  return !!(cards && cards.length === 5);
}

/**
 * Gibt den höchsten Rang in den Karten zurück
 * @param {Array} cards - Die Karten
 * @returns {number} Der höchste Rang (0-12, wobei 12 = 2er, höchste Karte)
 */
function getHighestCardRank(cards) {
  if (!cards || cards.length === 0) return -1;
  return Math.max(...cards.map(c => c.rank));
}

/**
 * Prüft ob eine Karte einen niedrigen Rang hat
 * @param {Object} card - Die Karte
 * @param {number} maxValue - Maximaler Kartenwert (inklusiv, 0=3, 1=4, 2=5, 3=6, etc.)
 * @returns {boolean} True wenn card.value <= maxValue
 */
function isLowCard(card, maxValue) {
  return !!(card && card.value <= maxValue);
}

/**
 * Prüft ob der PlayType in einer Liste von erlaubten Typen ist
 * @param {string} playType - Der aktuelle Play-Type
 * @param {Array<string>} allowedTypes - Liste erlaubter Types
 * @returns {boolean}
 */
function matchesPlayType(playType, allowedTypes) {
  return !!(allowedTypes && allowedTypes.includes(playType));
}

// =============================================================================
// EXPORTS
// =============================================================================

module.exports = {
  // Konstanten
  ranks,
  rankValues,
  suits,
  suitValues,
  PLAY_RANKS,

  // Deck-Funktionen
  createDeck,
  shuffle,
  dealCards,

  // Spieler-Funktionen
  findStartPlayer,

  // Spielzug-Validierung
  getPlayType,
  beatsLast,
  findValidPlays,

  // Karten-Aktionen
  drawCard,

  // Hilfsfunktionen
  combinations,
  getAllPairs,
  getAllTriples,
  hasCards,
  removeCards,

  // Combo-Detection Helper (für Siegel-Effekte)
  isAllSameColorGroup,
  isFiveCardPlay,
  getHighestCardRank,
  isLowCard,
  matchesPlayType,

  // Spezialkarten (re-export)
  isSpecialCard,
};
